import { useRef, useState } from 'react';
import classNames from 'classnames';

import EmojiSelect from '../EmojiSelect';
import { ColorPalette } from '../ColorPalette';
import { FontSizeSelect } from '../FontSizeSelect';
import { DEFAULT_TEXT_COLOR, largerFontSize, smallerFontSize } from '../../../functions/richText';
import OutsideClick from '../../../functions/OutsideClick';
import { usePopoverPlacement } from '../../../functions/popover';

// A toolbar button that leaves the focus and selection in the text field.
export function ToolbarButton({ title, className, active, onPress, children }) {
    return (
        <button
            type="button"
            title={title}
            className={classNames('panel-btn', className, { 'panel-btn_active': active })}
            onMouseDown={(e) => { e.preventDefault(); onPress(e); }}
        >
            {children}
        </button>
    );
}

// Word's formatting shortcuts, by key position so that they work in any keyboard layout: the action for a
// keydown, or null.
export const formatShortcut = (e) => {
    if (!(e.ctrlKey || e.metaKey) || e.altKey) return null;
    if (e.shiftKey) {
        // Alignment as in Google Docs; its Ctrl+Shift+J for justify opens Chrome's console instead.
        return {
            Period: 'grow', Comma: 'shrink', KeyS: 'strike', KeyL: 'alignLeft', KeyE: 'alignCenter', KeyR: 'alignRight',
        }[e.code] ?? null;
    }
    return { BracketRight: 'growByOne', BracketLeft: 'shrinkByOne', Space: 'clear', Backslash: 'clear' }[e.code] ?? null;
};

// The formatting the format painter copies, from a toolbar's state.
export const paintedFormat = ({ bold, italic, underline, strike, fontSize, color, highlight }) => (
    { bold, italic, underline, strike, fontSize, color, highlight }
);

const PAINTER = (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round">
        <rect x="3" y="3" width="15" height="6" rx="1.2" />
        <path d="M18 6h2.5v5H11v3" />
        <rect x="9.5" y="14" width="3" height="7" rx="1" />
    </svg>
);

const CLEAR = (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <path d="M5 5h12M11 5l-3 14M15 15l6 6M21 15l-6 6" />
    </svg>
);

const alignIcon = (lines) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <path d={lines} />
    </svg>
);

const ALIGNMENTS = [
    { value: 'left', label: 'По левому краю (Ctrl+Shift+L)', icon: alignIcon('M4 6h16M4 10h10M4 14h16M4 18h10') },
    { value: 'center', label: 'По центру (Ctrl+Shift+E)', icon: alignIcon('M4 6h16M7 10h10M4 14h16M7 18h10') },
    { value: 'right', label: 'По правому краю (Ctrl+Shift+R)', icon: alignIcon('M4 6h16M10 10h10M4 14h16M10 18h10') },
    { value: 'justify', label: 'По ширине', icon: alignIcon('M4 6h16M4 10h16M4 14h16M4 18h16') },
];

// A button showing the text's alignment, with a list to change it: value and onChange are 'left', 'center',
// 'right' or 'justify'.
function AlignSelect({ value, onChange }) {
    const ref = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const { above } = usePopoverPlacement(ref, isOpen, 0, 180);
    const current = ALIGNMENTS.find((a) => a.value === value) ?? ALIGNMENTS[0];

    OutsideClick(ref, () => setIsOpen(false));

    return (
        <span className="align-select" ref={ref}>
            <ToolbarButton title="Выравнивание" active={isOpen} onPress={() => setIsOpen(!isOpen)}>{current.icon}</ToolbarButton>
            {isOpen && (
                <ul className={classNames('align-list', { 'align-list_above': above })}>
                    {ALIGNMENTS.map((alignment) => (
                        <li
                            key={alignment.value}
                            className={classNames('align-option', { 'align-option_selected': alignment.value === current.value })}
                            // Picking closes the list, so the press mustn't reach the day window as one outside it.
                            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setIsOpen(false); onChange(alignment.value); }}
                        >
                            {alignment.icon}
                            <span>{alignment.label}</span>
                        </li>
                    ))}
                </ul>
            )}
        </span>
    );
}

const HIGHLIGHTER = (
    <svg width="18" height="16" viewBox="0 0 24 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round">
        <path d="M14.5 2.5l5 5-8 8H6.5v-5z" />
        <path d="M6.5 15.5l-3 3h5" />
    </svg>
);

// The formatting toolbar of the event form's text fields, as in Word: bold, italic, underline, strikethrough,
// font size, text colour, highlight and emojis. state is { bold, italic, underline, strike, fontSize, color,
// highlight }, and the on* callbacks apply a change: onToggle gets 'bold', 'italic', 'underline' or 'strike',
// and onHighlight a colour or null for none; onAlign gets an alignment, shown as state.align; onClear takes
// the formatting off. With onAutoSize the size box
// offers «Авто», shown when state.autoSize is set. painter is the form's format
// painter, { format, sticky } or null, shared by its fields: the brush copies this field's formatting at the
// caret for the next selection, or with a double click for every selection until Escape. The first row
// formats characters; the second has emojis, alignment and children, the field's own tools.
export function FormatToolbar({
    state, onToggle, onFontSize, onAutoSize, onColor, onHighlight, onAlign, onClear, onEmoji, painter, setPainter, children,
}) {
    const pressPainter = (e) => {
        if (e.detail >= 2) setPainter({ format: painter?.format ?? paintedFormat(state), sticky: true });
        else setPainter(painter ? null : { format: paintedFormat(state), sticky: false });
    };

    return (
        <div className="text-editor__panel">
            <div className="text-editor__row">
                <ToolbarButton title="Полужирный" className="icon-font-bold" active={state.bold} onPress={() => onToggle('bold')} />
                <ToolbarButton title="Курсив" className="icon-font-cursive" active={state.italic} onPress={() => onToggle('italic')} />
                <ToolbarButton title="Подчёркнутый" className="icon-font-underline" active={state.underline} onPress={() => onToggle('underline')} />
                <ToolbarButton title="Зачёркнутый" className="panel-btn_strike" active={state.strike} onPress={() => onToggle('strike')}>S</ToolbarButton>
                <FontSizeSelect value={state.autoSize ? 'auto' : state.fontSize} onChange={onFontSize} onAuto={onAutoSize} />
                <span className="font-size-steps">
                    <button type="button" title="Увеличить размер шрифта" className="font-size-step" onMouseDown={(e) => { e.preventDefault(); onFontSize(largerFontSize(state.fontSize)); }}>
                        A<sup>▲</sup>
                    </button>
                    <button type="button" title="Уменьшить размер шрифта" className="font-size-step font-size-step_down" onMouseDown={(e) => { e.preventDefault(); onFontSize(smallerFontSize(state.fontSize)); }}>
                        A<sup>▼</sup>
                    </button>
                </span>
                <ColorPalette title="Цвет текста" value={state.color} onChange={onColor} defaultColor={DEFAULT_TEXT_COLOR} />
                <ColorPalette title="Цвет выделения" value={state.highlight} onChange={onHighlight} noneLabel="Без выделения" icon={HIGHLIGHTER} />
                <ToolbarButton title="Формат по образцу (двойной щелчок — для нескольких фрагментов)" active={painter != null} onPress={pressPainter}>
                    {PAINTER}
                </ToolbarButton>
                <ToolbarButton title="Очистить формат (Ctrl+Пробел)" onPress={onClear}>{CLEAR}</ToolbarButton>
            </div>
            <div className="text-editor__row">
                <EmojiSelect addEmoji={onEmoji} />
                <AlignSelect value={state.align} onChange={onAlign} />
                {children}
            </div>
        </div>
    );
}
